import React, { FC, useState } from 'react';

import EmptyState from '@adsk/alloy-react-empty-state';
import { SimpleModal } from '@adsk/alloy-react-modal';
import ProgressRing from '@adsk/alloy-react-progress-ring';

import {
  bodyStyle,
  subscriptionsContainer,
  ErrorLabel,
  loadingSpinnerContainerStyles,
} from './helpers/styles';
import { Offering } from '../../helpers/types';
import { useTranslation } from '../../i18n';
import SubscriptionItem from '../SubscriptionItem/index';

interface SubscriptionsModalProps {
  onClose: () => void;
  subscriptions: Offering[];
  error: boolean;
  isLoading: boolean;
  onClickTryAgain: () => void;
}
interface ExpandedItem {
  [key: string]: boolean;
}

const SubscriptionsModal: FC<SubscriptionsModalProps> = ({
  onClose,
  subscriptions,
  error,
  isLoading,
  onClickTryAgain,
}) => {
  // First item is always expanded
  const [expandedItems, setExpandedItems] = useState<ExpandedItem>({ 0: true });
  const { t } = useTranslation();
  const hasSubscriptions = subscriptions?.length > 0;

  const LoadingSpinner = () => {
    return (
      <div
        data-testid="SubscriptionsModal__LoadingSpinner"
        style={loadingSpinnerContainerStyles}
      >
        <ProgressRing />
      </div>
    );
  };

  const EmptyStateLabel = () => {
    if (error) {
      return (
        <>
          <ErrorLabel data-testid="SubscriptionModal_EmptyState_Label">
            {t('FETCH_SUBSCRIPTIONS_ERROR')}
          </ErrorLabel>
          <a onClick={onClickTryAgain} style={{ cursor: 'pointer' }}>
            {t('TRY_AGAIN')}
          </a>
        </>
      );
    }
    return (
      <ErrorLabel data-testid="SubscriptionModal_ErrorState_Label">
        {t('NO_SUBSCRIPTIONS_ACTIVE')}
      </ErrorLabel>
    );
  };

  const ModalContent = () => {
    if (isLoading) {
      return <LoadingSpinner />;
    }

    if (hasSubscriptions && !error) {
      return (
        <div style={subscriptionsContainer}>
          {subscriptions.map((subscription: Offering, index) => {
            return (
              <SubscriptionItem
                key={`${subscription.offeringName}_${index}`}
                subscription={subscription}
                expanded={expandedItems[index]}
                onExpand={() => {
                  const newExpand = {
                    ...expandedItems,
                    [index]: !expandedItems[index],
                  };

                  setExpandedItems(newExpand);
                }}
              />
            );
          })}
        </div>
      );
    }
    return (
      <EmptyState
        data-testid="SubscriptionsModal__EmptyState"
        illustrationType="buildingConstructionGrey"
      >
        <EmptyStateLabel />
      </EmptyState>
    );
  };

  return (
    <SimpleModal
      title={t('MY_SUBSCRIPTIONS')}
      onClose={onClose}
      show
      modalBodyProps={{ style: bodyStyle }}
    >
      <ModalContent />
    </SimpleModal>
  );
};

SubscriptionsModal.displayName = 'SubscriptionsModal';

export default SubscriptionsModal;
